import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  LinearProgress,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import { fetchAllGroups } from '../../../controllers/groupManagementController';
import { searchMembers } from '../../../controllers/memberManagementController';
import {
  fetchBulkCreateDuesStatus,
  fetchCurrentDuesPolicy,
  startBulkCreateDues,
} from '../../../controllers/paymentsController';
import { BULK_DUES_JOB_STORAGE_KEY } from '../duesConstants';
import { buildDuesFormDefaults, toApiDateTime } from '../duesFormUtils';
import { formatPaymentOperationError } from '../../../utils/logoPrerequisiteErrors';

const fieldSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '5px' },
  '& .MuiInputBase-input': { fontSize: '14px' },
};

const TARGET_ALL = 'all';
const TARGET_GROUPS = 'groups';
const TARGET_MEMBERS = 'members';

const BulkCreateDuesDialog = ({ open, onClose, onCompleted }) => {
  const [form, setForm] = useState({ period: '', amount: '', dueDate: '', finalDueDate: '', description: '' });
  const [target, setTarget] = useState(TARGET_ALL);
  const [groups, setGroups] = useState([]);
  const [selectedGroups, setSelectedGroups] = useState([]);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberOptions, setMemberOptions] = useState([]);
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [memberLoading, setMemberLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  };

  const pollJob = (id) => {
    stopPolling();
    const tick = async () => {
      try {
        const status = await fetchBulkCreateDuesStatus(id);
        setJobStatus(status);
        if (status.state === 2 || status.state === 3) {
          stopPolling();
          localStorage.removeItem(BULK_DUES_JOB_STORAGE_KEY);
          setSubmitting(false);
          if (status.state === 2) {
            toast.success(`Toplu aidat tamamlandı. Oluşturulan: ${status.createdCount}, Atlanan: ${status.skippedCount}, Hatalı: ${status.failedCount}`);
            if (onCompleted) onCompleted();
          } else {
            toast.error(status.message || 'Toplu aidat işlemi başarısız');
          }
        }
      } catch (e) {
        stopPolling();
        setSubmitting(false);
        setError(e?.message || 'Durum alınamadı');
      }
    };
    tick();
    pollRef.current = setInterval(tick, 2500);
  };

  useEffect(() => {
    if (!open) {
      stopPolling();
      return undefined;
    }
    setError('');
    (async () => {
      try {
        const policy = await fetchCurrentDuesPolicy();
        setForm(buildDuesFormDefaults(policy));
      } catch {
        setForm(buildDuesFormDefaults(null));
      }
      try {
        const g = await fetchAllGroups();
        setGroups(Array.isArray(g) ? g : []);
      } catch {
        setGroups([]);
      }
    })();

    const savedJobId = localStorage.getItem(BULK_DUES_JOB_STORAGE_KEY);
    if (savedJobId) {
      setJobId(savedJobId);
      setSubmitting(true);
      pollJob(savedJobId);
    }

    return () => stopPolling();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open || target !== TARGET_MEMBERS) return;
    if (!memberQuery || memberQuery.trim().length < 2) {
      setMemberOptions([]);
      return;
    }
    let active = true;
    setMemberLoading(true);
    searchMembers(memberQuery.trim())
      .then((data) => { if (active) setMemberOptions(Array.isArray(data) ? data : []); })
      .finally(() => { if (active) setMemberLoading(false); });
    return () => { active = false; };
  }, [memberQuery, open, target]);

  const handleChange = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
  };

  const handleStart = async () => {
    if (!form.period?.trim() || !form.amount || Number(form.amount) <= 0 || !form.dueDate) {
      setError('Dönem, tutar ve vade zorunludur');
      return;
    }
    if (target === TARGET_GROUPS && selectedGroups.length === 0) {
      setError('En az bir grup seçiniz');
      return;
    }
    if (target === TARGET_MEMBERS && selectedMembers.length === 0) {
      setError('En az bir üye seçiniz');
      return;
    }
    setSubmitting(true);
    setError('');
    setJobStatus(null);
    try {
      const payload = {
        period: form.period.trim(),
        amount: Number(form.amount),
        dueDate: toApiDateTime(form.dueDate),
        finalDueDate: form.finalDueDate ? toApiDateTime(form.finalDueDate) : null,
        description: form.description?.trim() || null,
        duesPolicyId: null,
        memberIds: target === TARGET_MEMBERS ? selectedMembers.map((m) => m.id) : null,
        groupIds: target === TARGET_GROUPS ? selectedGroups.map((g) => g.id) : null,
      };
      const result = await startBulkCreateDues(payload);
      const newJobId = result?.jobId;
      if (!newJobId) throw new Error('Job ID alınamadı');
      setJobId(newJobId);
      localStorage.setItem(BULK_DUES_JOB_STORAGE_KEY, newJobId);
      pollJob(newJobId);
      toast.info('Toplu aidat oluşturma başlatıldı');
    } catch (e) {
      setSubmitting(false);
      setError(formatPaymentOperationError(e, { bulk: true, actionLabel: 'Toplu aidat oluşturma' }));
    }
  };

  const progress = jobStatus?.totalMembers
    ? Math.round((jobStatus.processedCount / jobStatus.totalMembers) * 100)
    : 0;

  const showProgress = submitting && jobStatus;

  return (
    <Dialog open={open} onClose={showProgress ? undefined : onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ fontSize: '18px', fontWeight: 500, display: 'flex', justifyContent: 'space-between', pr: 3 }}>
        Toplu Aidat Kes
        <IconButton onClick={onClose} size="small" disabled={submitting && jobStatus?.state === 1}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {error && <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert>}
          {!showProgress ? (
            <>
              <TextField label="Dönem" value={form.period} onChange={handleChange('period')} fullWidth sx={fieldSx} />
              <TextField label="Tutar" type="number" value={form.amount} onChange={handleChange('amount')} fullWidth sx={fieldSx} />
              <TextField label="Vade" type="date" value={form.dueDate} onChange={handleChange('dueDate')} InputLabelProps={{ shrink: true }} fullWidth sx={fieldSx} />
              <TextField label="Son Ödeme" type="date" value={form.finalDueDate} onChange={handleChange('finalDueDate')} InputLabelProps={{ shrink: true }} fullWidth sx={fieldSx} />
              <TextField label="Açıklama" value={form.description} onChange={handleChange('description')} multiline rows={2} fullWidth sx={fieldSx} />
              <FormControl>
                <Typography sx={{ fontSize: '13px', fontWeight: 500, mb: 1 }}>Hedef üyeler</Typography>
                <RadioGroup value={target} onChange={(e) => setTarget(e.target.value)}>
                  <FormControlLabel value={TARGET_ALL} control={<Radio size="small" />} label="Tüm üyeler" />
                  <FormControlLabel value={TARGET_GROUPS} control={<Radio size="small" />} label="Grup seç" />
                  <FormControlLabel value={TARGET_MEMBERS} control={<Radio size="small" />} label="Üye seç" />
                </RadioGroup>
              </FormControl>
              {target === TARGET_GROUPS && (
                <Autocomplete
                  multiple
                  options={groups}
                  value={selectedGroups}
                  onChange={(_, v) => setSelectedGroups(v)}
                  getOptionLabel={(g) => g?.name || ''}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  renderInput={(params) => <TextField {...params} label="Gruplar" sx={fieldSx} />}
                />
              )}
              {target === TARGET_MEMBERS && (
                <Autocomplete
                  multiple
                  options={memberOptions}
                  loading={memberLoading}
                  value={selectedMembers}
                  onChange={(_, v) => setSelectedMembers(v)}
                  onInputChange={(_, v) => setMemberQuery(v)}
                  getOptionLabel={(m) => m?.fullName || ''}
                  isOptionEqualToValue={(a, b) => a?.id === b?.id}
                  renderInput={(params) => <TextField {...params} label="Üyeler" placeholder="Ara..." sx={fieldSx} />}
                />
              )}
            </>
          ) : (
            <Box>
              <Typography sx={{ fontSize: '14px', mb: 1 }}>
                {jobStatus.stateName || 'İşleniyor'} — {jobStatus.message || ''}
              </Typography>
              {jobStatus.totalMembers > 0 && (
                <>
                  <LinearProgress variant="determinate" value={progress} sx={{ mb: 1 }} />
                  <Stack direction="row" spacing={1} flexWrap="wrap">
                    <Chip size="small" label={`İşlenen: ${jobStatus.processedCount}/${jobStatus.totalMembers}`} />
                    <Chip size="small" color="success" label={`Oluşturulan: ${jobStatus.createdCount}`} />
                    <Chip size="small" label={`Atlanan: ${jobStatus.skippedCount}`} />
                    <Chip size="small" color="error" label={`Hatalı: ${jobStatus.failedCount}`} />
                  </Stack>
                </>
              )}
              {Array.isArray(jobStatus.items) && jobStatus.items.length > 0 && jobStatus.state === 2 && (
                <TableContainer sx={{ mt: 2, maxHeight: 280 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Üye</TableCell>
                        <TableCell>Sonuç</TableCell>
                        <TableCell>Açıklama</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {jobStatus.items.map((item, idx) => (
                        <TableRow key={`${item.memberId}-${idx}`}>
                          <TableCell>{item.memberName}</TableCell>
                          <TableCell>{item.outcome}</TableCell>
                          <TableCell>{item.reason || item.logoSlipNo || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={submitting && jobStatus?.state === 1} sx={{ textTransform: 'none' }}>
          {showProgress && jobStatus?.state === 2 ? 'Kapat' : 'İptal'}
        </Button>
        {!showProgress && (
          <Button variant="contained" onClick={handleStart} disabled={submitting} sx={{ textTransform: 'none', backgroundColor: '#0E4D94' }}>
            {submitting ? <CircularProgress size={18} color="inherit" /> : 'Başlat'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default BulkCreateDuesDialog;
